import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChefHat, Sparkles, ArrowRight, Download, Send, Heart } from "lucide-react";
import { api, API, prettyDate, formatApiError } from "../lib/api";
import { downloadRecipeBooklet } from "../lib/recipeBooklet";
import { Stars } from "../components/Stars";
import Reveal from "../components/Reveal";

function RecipeCard({ recipe, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal
      delay={index * 0.06}
      className="border border-[color:var(--rule)] bg-[color:var(--paper)] p-7 sm:p-9"
      data-testid={`recipe-card-${recipe.key}`}
    >
      <div className="flex items-baseline gap-4">
        <span className="font-display text-[color:var(--gold)] text-3xl sm:text-4xl leading-none">
          {recipe.number}
        </span>
        <div>
          <h3 className="font-display text-2xl sm:text-3xl leading-tight">{recipe.title}</h3>
          <p className="font-hand text-xl text-[color:var(--moss)]">{recipe.romanian}</p>
        </div>
      </div>
      <span className="rule" />
      <p className="font-body text-[color:var(--ink-2)] leading-relaxed italic">{recipe.blurb}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        <div>
          <p className="eyebrow mb-3">What goes in</p>
          <ul className="space-y-2">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="font-body text-[color:var(--ink)] leading-snug flex gap-3">
                <span className="text-[color:var(--gold)] mt-1">·</span>
                <span>{ing}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-3">How it comes together</p>
          <ol className="space-y-3">
            {recipe.steps.map((step, i) => (
              <li key={i} className="font-body text-[color:var(--ink)] leading-snug flex gap-3">
                <span className="font-display text-[color:var(--moss)] text-lg leading-none w-5 shrink-0">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-8 inline-flex items-center gap-2 text-[color:var(--moss)] hover:text-[color:var(--moss-dark)] transition-colors"
        data-testid={`reveal-mystery-${recipe.key}`}
      >
        <Sparkles className="w-4 h-4" />
        <span className="eyebrow !text-[color:var(--moss)]">
          {open ? "Hide the mystery ingredient" : `Reveal the mystery ingredient`}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-5 border-l-2 border-[color:var(--gold)] bg-[color:var(--paper-2)]/70 p-5">
              <p className="font-hand text-2xl text-[color:var(--moss)] leading-none mb-2">
                {recipe.mystery_ingredient}
              </p>
              <p className="font-body text-[color:var(--ink-2)] leading-relaxed">{recipe.reveal}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Reveal>
  );
}

function GuestReviewForm({ experienceId, defaultName }) {
  const [rating, setRating] = useState(5);
  const [authorName, setAuthorName] = useState(defaultName || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/experiences/${experienceId}/reviews`, {
        author_name: authorName,
        rating,
        note,
      });
      setDone(true);
    } catch {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border border-[color:var(--rule)] bg-[color:var(--paper-2)]/70 p-8 text-center" data-testid="review-thanks">
        <Heart className="w-7 h-7 text-[color:var(--gold)] mx-auto" />
        <p className="font-display text-2xl mt-4">Thank you — that means the world.</p>
        <p className="font-body text-[color:var(--ink-2)] mt-2">
          Your note is with Andrea now. She'll share it on the site once she's read it.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-[color:var(--rule)] bg-[color:var(--paper)] p-7 sm:p-9" data-testid="review-form">
      <p className="eyebrow text-[color:var(--moss)]">Leave Andrea a note</p>
      <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">How was your afternoon?</h3>
      <span className="rule" />

      <div className="flex items-center gap-4">
        <span className="letter-label !mb-0">Your rating</span>
        <Stars value={rating} onChange={setRating} size={26} testIdPrefix="review-star" />
      </div>

      <div className="mt-6">
        <label className="letter-label" htmlFor="review-name">Your name (as you'd like it shown)</label>
        <input id="review-name" className="letter-input" value={authorName} onChange={(e) => setAuthorName(e.target.value)} data-testid="review-name" />
      </div>

      <div className="mt-6">
        <label className="letter-label" htmlFor="review-note">A few warm words</label>
        <textarea id="review-note" className="letter-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did the afternoon feel like? What will you remember?" data-testid="review-note" required />
      </div>

      <button type="submit" className="btn-primary mt-7 inline-flex gap-2" disabled={busy} data-testid="review-submit">
        <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send my note to Andrea"}
      </button>
    </form>
  );
}

export default function GuestKeepsake() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get(`/experiences/${id}`)
      .then((res) => active && setData(res.data))
      .catch((e) =>
        active && setError(formatApiError(e.response?.data?.detail) || "This keepsake could not be found.")
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--paper)]" data-testid="keepsake-loading">
        <p className="font-hand text-3xl text-[color:var(--moss)] animate-pulse">Setting the table…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[color:var(--paper)] px-6 text-center" data-testid="keepsake-error">
        <ChefHat className="w-10 h-10 text-[color:var(--gold)]" />
        <p className="font-display text-3xl mt-6">This keepsake isn't set yet</p>
        <p className="font-body text-[color:var(--ink-2)] mt-3 max-w-md">{error}</p>
        <a href="/" className="btn-ghost mt-8">Visit theunfinishedlunch</a>
      </div>
    );
  }

  const photos = data.photos || [];

  return (
    <div className="paper-grain min-h-screen bg-[color:var(--paper)]" data-testid="keepsake-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-[14svh] pb-24">
        <Reveal>
          <p className="eyebrow text-[color:var(--moss)]">A keepsake from your afternoon</p>
          <h1 className="font-display mt-5 text-[clamp(2.75rem,7vw,5.5rem)] leading-[0.9] tracking-tight">
            Thank you,<br />
            <em className="italic text-[color:var(--gold)]">{data.guest_name}.</em>
          </h1>
          {data.visit_date && (
            <p className="font-hand text-2xl text-[color:var(--moss)] mt-4">
              {prettyDate(data.visit_date)}, at my table in Cluj-Napoca
            </p>
          )}
        </Reveal>

        {data.note && (
          <Reveal delay={0.1} className="mt-10">
            <div className="border-l-2 border-[color:var(--gold)] pl-6">
              <p className="font-body text-xl sm:text-2xl text-[color:var(--ink)] leading-relaxed italic drop-cap">
                {data.note}
              </p>
              <p className="font-hand text-2xl text-[color:var(--moss)] mt-4">— Andrea</p>
            </div>
          </Reveal>
        )}

        {photos.length > 0 && (
          <section className="mt-16" data-testid="keepsake-gallery">
            <Reveal>
              <p className="eyebrow">Your afternoon, in pictures</p>
              <span className="rule" />
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {photos.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.05}>
                  <button
                    onClick={() => setLightbox(p.id)}
                    className="block w-full aspect-square overflow-hidden bg-[color:var(--paper-2)] photo-shadow group"
                    data-testid={`gallery-photo-${p.id}`}
                  >
                    <img
                      src={`${API}/photos/${p.id}`}
                      alt={p.original_filename || "A moment from the lunch"}
                      loading="lazy"
                      className="photo transition-transform duration-700 group-hover:scale-105"
                    />
                  </button>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {data.recipes?.length > 0 && (
          <section className="mt-20" data-testid="keepsake-recipes">
            <Reveal>
              <p className="eyebrow">The recipes you lived through</p>
              <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight">
                Take the afternoon home with you.
              </h2>
              <p className="font-body text-[color:var(--ink-2)] mt-3 max-w-xl leading-relaxed">
                Everything we cooked together — and the little secrets that turned each old recipe
                into something of its own.
              </p>
              <button
                onClick={() => downloadRecipeBooklet(data)}
                className="btn-ghost mt-6 inline-flex gap-2"
                data-testid="download-recipes-btn"
              >
                <Download className="w-4 h-4" /> Download the recipe booklet
              </button>
              <span className="rule" />
            </Reveal>
            <div className="space-y-6">
              {data.recipes.map((r, i) => (
                <RecipeCard key={r.key} recipe={r} index={i} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-20" data-testid="keepsake-review-section">
          <Reveal>
            <GuestReviewForm experienceId={data.id} defaultName={data.guest_name} />
          </Reveal>
        </section>

        <Reveal className="mt-20 text-center">
          <p className="font-display text-2xl sm:text-3xl leading-snug">
            The only thing missing, next time, <em className="italic text-[color:var(--gold)]">is a return.</em>
          </p>
          <a
            href="https://wa.me/40746264632"
            target="_blank"
            rel="noreferrer"
            className="btn-primary mt-8 inline-flex gap-3"
            data-testid="keepsake-book-again"
          >
            Come back to the table <ArrowRight className="w-4 h-4" />
          </a>
          <p className="font-hand text-xl text-[color:var(--moss)] mt-10">theunfinishedlunch · by Andrea, Cluj-Napoca</p>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[color:var(--ink)]/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 text-[color:var(--paper)] hover:text-[color:var(--gold)] transition-colors"
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={`${API}/photos/${lightbox}`}
              alt="A moment from the lunch"
              className="max-h-[88svh] max-w-full object-contain photo-shadow"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
